import { Mic, MicOff, TriangleAlert } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';

import { usePreviewAudio, usePreviewMode } from '../lib/previewMode';
import { cn } from '../lib/utils';

interface TalkbackProps {
  /** WHIP endpoint (`https://.../rtc/v1/whip/?app=talkback&stream=<key>`).
   *  Null hides the control (WebRTC isn't configured). */
  publishUrl: string | null;
  /** Who the operator is talking to — used in the helper copy. */
  correspondentName?: string | null;
  className?: string;
}

/**
 * Push-to-talk into the correspondent's earpiece (IFB).
 *
 * The operator's microphone is WHIP-published to SRS as an audio-only
 * Opus track under the `talkback` app; the correspondent's phone
 * WHEP-subscribes to the same `talkback/<streamKey>` and plays it. That
 * app is deliberately separate from `live` — nothing said here can be
 * mixed into the outbound broadcast, the DVR recording, or a studio
 * pull. See `buildTalkbackUrl` in the API's liveStreamService.
 *
 * **Why the connection is opened once and then muted, not torn down
 * between presses.** A WHIP handshake is a getUserMedia prompt plus an
 * SDP round-trip plus ICE — several hundred milliseconds on a good day.
 * Paying that on every key of the mic would clip the first word of
 * every sentence, which is precisely the word ("stand by", "wrap",
 * "cut") that matters. So the first press connects and stays connected;
 * subsequent presses just flip `track.enabled`. A disabled track still
 * sends silence, which is a bonus: SRS keeps the talkback source alive
 * between presses, so the correspondent's subscription doesn't have to
 * re-establish either.
 *
 * The trade-off is that the browser's mic indicator stays lit for the
 * whole session. That's the honest signal — the tab does hold the mic
 * — and the button's own state is unambiguous about whether anything
 * is actually being transmitted.
 *
 * **Echo.** The correspondent's phone plays this out of its earpiece by
 * default and the platform's acoustic echo canceller handles the rest,
 * but a correspondent on speakerphone in a quiet room will feed the
 * operator back to themselves. Push-to-talk bounds that window; a wired
 * earpiece eliminates it, which is the field guidance either way.
 */
type TalkbackStatus = 'idle' | 'connecting' | 'ready' | 'error';

/**
 * How long a connected-but-silent channel is held open before we hang
 * up. Long enough to cover the natural gaps in a briefing, short enough
 * that an operator who wandered off isn't holding a mic capture and an
 * SRS session for the rest of the broadcast.
 */
const IDLE_HANGUP_MS = 120_000;

export function Talkback({
  publishUrl,
  correspondentName,
  className,
}: TalkbackProps): JSX.Element | null {
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [status, setStatus] = useState<TalkbackStatus>('idle');
  const [transmitting, setTransmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const previewMode = usePreviewMode();
  const previewAudio = usePreviewAudio();

  const teardown = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    // Stop the tracks before closing the peer connection so the
    // browser's mic indicator goes out promptly rather than lingering
    // until GC.
    for (const t of streamRef.current?.getTracks() ?? []) t.stop();
    streamRef.current = null;
    pcRef.current?.close();
    pcRef.current = null;
    setTransmitting(false);
    setStatus('idle');
  }, []);

  // Drop the channel when the card unmounts or the session changes —
  // otherwise an operator who navigates away leaves a hot mic pointed
  // at a stream that may since have been handed to someone else.
  // `publishUrl` isn't a dependency because it can't change under a
  // mounted instance: every caller renders these inside a card keyed by
  // session id, so a different session is a different component.
  useEffect(() => teardown, [teardown]);

  const armIdleHangup = useCallback(() => {
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(teardown, IDLE_HANGUP_MS);
  }, [teardown]);

  /** Open the WHIP session. Resolves to the live audio track, or null. */
  const connect = useCallback(async (): Promise<MediaStreamTrack | null> => {
    if (!publishUrl) return null;
    setStatus('connecting');
    setError(null);
    try {
      const media = await navigator.mediaDevices.getUserMedia({
        // The full DSP chain: the operator is in a newsroom, on a
        // laptop mic, listening to the correspondent on speakers. Any
        // of the three defaults being off makes this unusable.
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
        video: false,
      });
      streamRef.current = media;
      const track = media.getAudioTracks()[0] ?? null;
      if (!track) throw new Error('no microphone track');
      // Start muted: connecting is not the same as talking, and the
      // press that triggered this enables it explicitly below.
      track.enabled = false;

      const pc = new RTCPeerConnection({
        iceServers: [{ urls: 'stun:stun.l.google.com:19302' }],
      });
      pcRef.current = pc;
      // Send-only: we're the publisher. Offering a recvonly direction
      // would make SRS negotiate a return track it has nothing to put
      // on — the correspondent's audio comes back through the preview
      // player, not through this connection.
      pc.addTransceiver(track, { direction: 'sendonly', streams: [media] });

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      const res = await fetch(publishUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/sdp' },
        body: offer.sdp ?? '',
      });
      if (!res.ok) throw new Error(`WHIP publish failed (${res.status})`);
      const answer = await res.text();
      await pc.setRemoteDescription({ type: 'answer', sdp: answer });

      setStatus('ready');
      return track;
    } catch (err) {
      teardown();
      setStatus('error');
      setError(
        err instanceof DOMException && err.name === 'NotAllowedError'
          ? 'Microphone blocked — allow mic access for this site.'
          : err instanceof Error
            ? err.message
            : 'Could not open talkback.',
      );
      return null;
    }
  }, [publishUrl, teardown]);

  const startTalking = useCallback(async () => {
    if (transmitting) return;
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    const existing = streamRef.current?.getAudioTracks()[0] ?? null;
    const track = existing ?? (await connect());
    if (!track) return;
    track.enabled = true;
    setTransmitting(true);
  }, [connect, transmitting]);

  const stopTalking = useCallback(() => {
    const track = streamRef.current?.getAudioTracks()[0];
    if (track) track.enabled = false;
    setTransmitting(false);
    // Only arm the hangup if we actually have a channel to hang up —
    // a failed connect already tore everything down.
    if (streamRef.current) armIdleHangup();
  }, [armIdleHangup]);

  // A pointerup that lands outside the button (operator drags off while
  // holding) never reaches our handler, so listen on the window while
  // transmitting. Without this the mic latches open — the exact failure
  // push-to-talk exists to prevent.
  useEffect(() => {
    if (!transmitting) return;
    const release = () => stopTalking();
    window.addEventListener('pointerup', release);
    window.addEventListener('pointercancel', release);
    // Losing focus (alt-tab, screen lock) should also close the mic.
    window.addEventListener('blur', release);
    return () => {
      window.removeEventListener('pointerup', release);
      window.removeEventListener('pointercancel', release);
      window.removeEventListener('blur', release);
    };
  }, [transmitting, stopTalking]);

  if (!publishUrl) return null;

  const busy = status === 'connecting';
  const who = correspondentName?.trim() || 'the correspondent';

  return (
    <div className={cn('space-y-1.5', className)}>
      <button
        type="button"
        // Pointer events cover mouse, pen and touch in one path.
        onPointerDown={(e) => {
          // Keep the press from also selecting text / starting a drag,
          // which on some browsers swallows the matching pointerup.
          e.preventDefault();
          void startTalking();
        }}
        // Keyboard parity: hold Space or Enter. `repeat` guards against
        // key-repeat firing startTalking dozens of times per second.
        onKeyDown={(e) => {
          if (e.repeat) return;
          if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            void startTalking();
          }
        }}
        onKeyUp={(e) => {
          if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            stopTalking();
          }
        }}
        onBlur={() => {
          if (transmitting) stopTalking();
        }}
        disabled={busy}
        aria-pressed={transmitting}
        aria-label={`Hold to talk to ${who}`}
        className={cn(
          'inline-flex h-9 w-full select-none items-center justify-center gap-2 rounded-md border text-sm font-medium transition-colors duration-100',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 focus-visible:ring-offset-2',
          'disabled:cursor-not-allowed disabled:opacity-60',
          transmitting
            ? 'border-danger bg-danger text-white'
            : 'border-line bg-surface text-ink hover:border-line-strong hover:bg-surface-tinted',
        )}
      >
        {transmitting ? (
          <>
            {/* The pulse is the at-a-glance "your mic is open" signal —
             *  colour alone is easy to miss on a dense green-room grid. */}
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-white" />
            </span>
            <Mic className="h-4 w-4" />
            Talking…
          </>
        ) : (
          <>
            {status === 'error' ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
            {busy ? 'Opening mic…' : 'Hold to talk'}
          </>
        )}
      </button>

      {error ? (
        <p className="text-xs text-danger">{error}</p>
      ) : !previewAudio ? (
        // Talkback itself works whatever the preview is doing — it's a
        // separate peer connection. But with preview audio off the
        // operator can't hear the replies to what they just said.
        <p className="flex items-start gap-1.5 text-xs text-ink-muted">
          <TriangleAlert className="mt-px h-3.5 w-3.5 shrink-0" />
          <span>Preview audio is off. Turn Audio on above to hear the correspondent's replies.</span>
        </p>
      ) : previewMode === 'hls' ? (
        // HLS runs 6-8 s behind while the correspondent hears the
        // operator instantly, so an operator who keeps talking ends up
        // over the top of a reply they haven't heard yet. WebRTC
        // carries the audio too, at under a second. Worth a nudge, not
        // worth blocking.
        <p className="flex items-start gap-1.5 text-xs text-ink-muted">
          <TriangleAlert className="mt-px h-3.5 w-3.5 shrink-0" />
          <span>
            Preview is on HLS (6-8 s behind). Switch to WebRTC above for a natural back-and-forth.
          </span>
        </p>
      ) : null}
    </div>
  );
}
